import React, { MutableRefObject } from "react";

import {
    KeyboardNavigationProps,
    MenuItemElement,
    NavigationMenuItem,
} from "./navigationTypes";
import {
    focusElementOnNextFrame,
    focusRefAfterRender,
    focusSecondLevelItem,
} from "./navigationFocusUtils";

type SetState<T> = React.Dispatch<React.SetStateAction<T>>;
type TabDirection = "forward" | "backward";

interface TopLevelKeyboardNavArgs {
    event: React.KeyboardEvent;
    index: number;
    menuItem: NavigationMenuItem;
    activeMenuItem: NavigationMenuItem | null;
    setIsMenuOpen: SetState<boolean>;
    setActiveMenuItem: SetState<NavigationMenuItem | null>;
    setFocusedMenuItemIndex: SetState<number | null>;
    lastFocusedMenuItemIndex: MutableRefObject<number | null>;
    firstPopoverItemRef: MutableRefObject<MenuItemElement | null>;
}

interface SecondLevelItemKeyDownArgs {
    event: React.KeyboardEvent;
    index: number;
    props: KeyboardNavigationProps;
    lastActiveSecondLevelIndexRef: MutableRefObject<number | undefined>;
}

interface ThirdLevelItemKeyDownArgs {
    event: React.KeyboardEvent;
    isLastItem: boolean;
    isFirstItem: boolean;
    lastActiveSecondLevelIndexRef: MutableRefObject<number | undefined>;
    setThirdLevelMenuItems: SetState<NavigationMenuItem[]>;
    props: KeyboardNavigationProps;
}

const isTab = (event: React.KeyboardEvent): boolean => event.key === "Tab";
const isEnter = (event: React.KeyboardEvent): boolean => event.key === "Enter";
const isEscape = (event: React.KeyboardEvent): boolean => event.key === "Escape";

const getTabDirection = (event: React.KeyboardEvent): TabDirection | null => {
    if (!isTab(event)) {
        return null;
    }

    return event.shiftKey ? "backward" : "forward";
};

const hasChildren = (menuItem: NavigationMenuItem): boolean => {
    return Boolean(menuItem.children?.length);
};

const closeTopLevelMenu = (
    setIsMenuOpen: SetState<boolean>,
    setActiveMenuItem: SetState<NavigationMenuItem | null>,
    setFocusedMenuItemIndex: SetState<number | null>
): void => {
    setIsMenuOpen(false);
    setActiveMenuItem(null);
    setFocusedMenuItemIndex(null);
};

const closeAndFocusMainNavItem = (
    closeMenu: () => void,
    target: HTMLAnchorElement | null | undefined
): void => {
    closeMenu();
    focusElementOnNextFrame(target);
};

const closeThirdLevelMenu = (
    setThirdLevelMenuItems: SetState<NavigationMenuItem[]>
): void => {
    setThirdLevelMenuItems([]);
};

const getSecondLevelItemsCount = (props: KeyboardNavigationProps): number => {
    return props.selection?.children?.length ?? 0;
};

const getSecondLevelIndexAfterThirdLevelTab = (
    currentIndex: number,
    direction: TabDirection
): number => {
    return direction === "forward" ? currentIndex + 1 : currentIndex;
};

export const handleMenuItemKeyboardNav = ({
                                              event,
                                              index,
                                              menuItem,
                                              activeMenuItem,
                                              setIsMenuOpen,
                                              setActiveMenuItem,
                                              setFocusedMenuItemIndex,
                                              lastFocusedMenuItemIndex,
                                              firstPopoverItemRef,
                                          }: TopLevelKeyboardNavArgs): void => {
    lastFocusedMenuItemIndex.current = index;

    if (isTab(event)) {
        closeTopLevelMenu(
            setIsMenuOpen,
            setActiveMenuItem,
            setFocusedMenuItemIndex
        );
        return;
    }

    if (isEscape(event)) {
        event.preventDefault();
        closeTopLevelMenu(
            setIsMenuOpen,
            setActiveMenuItem,
            setFocusedMenuItemIndex
        );
        return;
    }

    if (!isEnter(event)) {
        return;
    }

    if (!hasChildren(menuItem)) {
        return;
    }

    event.preventDefault();

    if (activeMenuItem === menuItem) {
        closeTopLevelMenu(
            setIsMenuOpen,
            setActiveMenuItem,
            setFocusedMenuItemIndex
        );
        return;
    }

    setActiveMenuItem(menuItem);
    setIsMenuOpen(true);
    setFocusedMenuItemIndex(index);
    focusRefAfterRender(firstPopoverItemRef);
};

const handleTabOutOfThirdLevelItems = ({
                                           direction,
                                           lastActiveSecondLevelIndexRef,
                                           setThirdLevelMenuItems,
                                           props,
                                       }: {
    direction: TabDirection;
    lastActiveSecondLevelIndexRef: MutableRefObject<number | undefined>;
    setThirdLevelMenuItems: SetState<NavigationMenuItem[]>;
    props: KeyboardNavigationProps;
}): void => {
    closeThirdLevelMenu(setThirdLevelMenuItems);

    const currentSecondLevelIndex = lastActiveSecondLevelIndexRef.current ?? 0;
    const secondLevelItemsCount = getSecondLevelItemsCount(props);

    const leavesFlyoutForward =
        direction === "forward" &&
        currentSecondLevelIndex >= secondLevelItemsCount - 1;

    if (leavesFlyoutForward) {
        closeAndFocusMainNavItem(props.closeMenu, props.getMainNavItemRef(1));
        return;
    }

    const targetSecondLevelIndex = getSecondLevelIndexAfterThirdLevelTab(
        currentSecondLevelIndex,
        direction
    );

    focusSecondLevelItem(targetSecondLevelIndex);
    lastActiveSecondLevelIndexRef.current = targetSecondLevelIndex;
};

export const handleSecondLevelItemKeyDown = ({
                                                 event,
                                                 index,
                                                 props,
                                                 lastActiveSecondLevelIndexRef,
                                             }: SecondLevelItemKeyDownArgs): void => {
    const direction = getTabDirection(event);
    const { getMainNavItemRef, closeMenu, selection } = props;

    const secondLevelItemsCount = selection?.children?.length ?? 0;
    const isFirstItem = index === 0;
    const isLastItem = index === secondLevelItemsCount - 1;

    if (direction) {
        lastActiveSecondLevelIndexRef.current =
            direction === "backward" ? index - 1 : index;
    }

    if (isEscape(event)) {
        event.preventDefault();
        closeAndFocusMainNavItem(closeMenu, getMainNavItemRef(0));
        return;
    }

    if (direction === "forward" && isLastItem) {
        event.preventDefault();
        closeAndFocusMainNavItem(closeMenu, getMainNavItemRef(1));
        return;
    }

    if (direction === "backward" && isFirstItem) {
        event.preventDefault();
        closeAndFocusMainNavItem(closeMenu, getMainNavItemRef(0));
    }
};

export const handleThirdLevelItemKeyDown = ({
                                                event,
                                                isLastItem,
                                                isFirstItem,
                                                lastActiveSecondLevelIndexRef,
                                                setThirdLevelMenuItems,
                                                props,
                                            }: ThirdLevelItemKeyDownArgs): void => {
    const direction = getTabDirection(event);

    if (isEscape(event)) {
        event.preventDefault();
        closeThirdLevelMenu(setThirdLevelMenuItems);
        focusSecondLevelItem(lastActiveSecondLevelIndexRef.current);
        return;
    }

    if (direction === "forward" && isLastItem) {
        event.preventDefault();
        handleTabOutOfThirdLevelItems({
                                          direction,
                                          lastActiveSecondLevelIndexRef,
                                          setThirdLevelMenuItems,
                                          props,
                                      });
        return;
    }

    if (direction === "backward" && isFirstItem) {
        event.preventDefault();
        handleTabOutOfThirdLevelItems({
                                          direction,
                                          lastActiveSecondLevelIndexRef,
                                          setThirdLevelMenuItems,
                                          props,
                                      });
    }
};